import type {
  VisibilityRuleRead,
  VisibilityWireOperator,
} from '@/api/customFields/formulaFields.interface';

// One condition as the server stores it: the field's handle, not its token.
interface WireCondition {
  field?: string;
  operator?: VisibilityWireOperator | string;
  value?: unknown;
}

// What the form holds for a handle, and whether that field counts as a number.
export interface VisibilityContext {
  valueOf: (handle: string) => unknown;
  isNumeric: (handle: string) => boolean;
}

// Kinds the server compares as numbers: NUMBER_KINDS on AtsCustomField, plus
// the `decimal` a static column arrives as. Lives here rather than beside the
// drawer's condition options because both the settings page and the offer form
// need it, and a component may not import from a page.
const NUMBER_KINDS = ['number', 'currency', 'percent', 'decimal'];

export const isNumberConditionKind = (kind?: string) =>
  NUMBER_KINDS.includes(kind ?? '');

const toList = (value: unknown) =>
  value === null || value === undefined
    ? []
    : Array.isArray(value)
      ? value
      : [value];

const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

// The rule stores 122 as JSON; the offer's salary arrives as a decimal, so
// "0.122e3" never equals "122" as text. Both sides are coerced when the field
// is a number, which is what ConditionEvaluator#same_value? does server-side.
const sameValue = (actual: unknown, expected: unknown, numeric: boolean) => {
  if (numeric) {
    const a = Number(actual);
    const b = Number(expected);
    if (!Number.isNaN(a) && !Number.isNaN(b)) return a === b;
  }
  return String(actual) === String(expected);
};

const compare = (
  operator: string,
  actual: unknown,
  expected: unknown,
  numeric: boolean
) => {
  const a = Number(actual);
  const b = Number(expected);
  const ordered = !Number.isNaN(a) && !Number.isNaN(b);

  switch (operator) {
    case 'eq':
      return sameValue(actual, expected, numeric);
    case 'neq':
      return !sameValue(actual, expected, numeric);
    case 'gt':
      return ordered && a > b;
    case 'gte':
      return ordered && a >= b;
    case 'lt':
      return ordered && a < b;
    case 'lte':
      return ordered && a <= b;
    case 'any_in':
    case 'all_in': {
      const held = toList(actual);
      const wanted = toList(expected);
      const matches = (want: unknown) =>
        held.some((one) => sameValue(one, want, numeric));
      return operator === 'all_in'
        ? wanted.every(matches)
        : wanted.some(matches);
    }
    case 'none_in':
      return !toList(expected).some((want) =>
        toList(actual).some((one) => sameValue(one, want, numeric))
      );
    case 'is_empty':
      return isBlank(actual);
    case 'is_present':
      return !isBlank(actual);
    default:
      // An operator this build does not know must not silently hide the field.
      return true;
  }
};

/**
 * Whether a field carrying this visibility rule should show, given what the
 * form holds right now.
 *
 * The server computes the same answer once, at load, and sends it as
 * `hidden_by_condition`. That is enough for a saved offer opened cold and no
 * use at all while someone is typing: the condition has to be re-read on every
 * keystroke, and on the creation screen there is no offer to have computed it
 * from in the first place.
 *
 * No rule, or a rule with nothing in it, means the field always shows.
 */
export const isVisibleByRule = (
  rule: VisibilityRuleRead | undefined,
  context: VisibilityContext
): boolean => {
  const conditions = (rule?.conditions ?? []) as WireCondition[];
  const usable = conditions.filter((condition) => condition?.field);
  if (usable.length === 0) return true;

  const met = (condition: WireCondition) =>
    compare(
      String(condition.operator ?? 'eq'),
      context.valueOf(String(condition.field)),
      condition.value,
      context.isNumeric(String(condition.field))
    );

  return rule?.match === 'any' ? usable.some(met) : usable.every(met);
};
